/**
 * The player, in its two sizes.
 *
 * Full: opens when a practice begins. It takes the whole screen and nothing
 * behind it moves - the page is locked in place, so there is no scrollbar and
 * no way to drag the player off the page. The cover sits over a blurred wash
 * of itself; under it are the scrubber, the transport, and a row of chips for
 * the things people change mid-sit (speed, bells, the end timer, tracks).
 *
 * Mini: the same practice, minimised to a card that floats above the tab bar
 * on a phone and along the bottom on a desk, so the rest of the app can be
 * used while it plays. Tapping it opens the full player again.
 */
import { useEffect, useRef, useState } from 'react';
import { formatClock } from '@zenport/shared';
import { DoneChip, DoneTick } from '../components/DoneTick.tsx';
import { usePlayer } from './PlayerProvider.tsx';
import { playBell } from './bell.ts';
import { Cover, Icon, Sheet, Slider, Switch } from '../components/ui.tsx';
import { useScrollLock } from '../scrollLock.ts';

const SPEEDS = [0.75, 0.9, 1, 1.1, 1.25] as const;
const LEAD_INS = [
  { v: 0, label: 'Off' },
  { v: 10, label: '10 s' },
  { v: 30, label: '30 s' },
  { v: 60, label: '1 min' },
] as const;
const BELLS = [0, 5, 10, 15, 20] as const;
const END_AFTER = [0, 10, 20, 30, 45, 60] as const;

const minLabel = (m: number) => (m === 0 ? 'Off' : m === 60 ? '1 h' : `${m} min`);
const speedLabel = (v: number) => `${v}×`;
/** Segment label: short enough for six in a row on a phone. */
const minShort = (m: number) => (m === 0 ? 'Off' : m === 60 ? '1h' : `${m}m`);

// ── Scrubber ───────────────────────────────────────────────────────────────

/**
 * A range input that previews while dragged and seeks once on release, so a
 * drag across an hour-long talk is one seek, not a hundred.
 */
function Scrubber({ compact = false }: { compact?: boolean }) {
  const p = usePlayer();
  const [preview, setPreview] = useState<number | null>(null);
  const max = Math.max(1, Math.floor(p.duration));
  const value = preview ?? Math.min(Math.floor(p.position), max);
  const known = p.duration > 0;
  return (
    <div className={`scrub${compact ? ' compact' : ''}`}>
      <Slider
        className="scrub-slider"
        value={value}
        max={max}
        keyStep={5}
        disabled={!known}
        onInput={setPreview}
        onCommit={(v) => {
          p.seek(v);
          setPreview(null);
        }}
        label="Position"
        valueText={`${formatClock(value)} of ${known ? formatClock(p.duration) : 'unknown'}`}
      />
      {!compact && (
        <div className="scrub-times">
          <span>{formatClock(value)}</span>
          <span>{known ? `-${formatClock(Math.max(0, p.duration - value))}` : '–:––'}</span>
        </div>
      )}
    </div>
  );
}

// ── Mini player ────────────────────────────────────────────────────────────

export function PlayerBar() {
  const p = usePlayer();
  if (!p.item || p.focus) return null;
  const pct = p.duration > 0 ? Math.min(100, (p.position / p.duration) * 100) : 0;
  const sub =
    p.leadInRemaining !== null
      ? `Settling · ${p.leadInRemaining}s`
      : p.item.tracks.length > 1 && p.track
        ? p.track.title
        : p.item.creator;

  return (
    <div className="mini" role="region" aria-label={p.learning ? 'Now playing' : 'Now practicing'}>
      <div className="mini-progress" style={{ inlineSize: `${pct}%` }} aria-hidden="true" />
      <button className="mini-open" onClick={() => p.setFocus(true)} aria-label="Open the player">
        <Cover
          coverId={p.item.coverId}
          title={p.item.title}
          creator={p.item.creator}
          className="mini-cover"
        />
        <span className="mini-meta">
          <span className="t">{p.item.title}</span>
          <span className="s">{sub}</span>
        </span>
      </button>
      <div className="mini-desk">
        <Scrubber compact />
        <span className="mini-time">
          {formatClock(p.position)} / {p.duration ? formatClock(p.duration) : '–:––'}
        </span>
      </div>
      <div className="mini-controls">
        <button
          className="icon-btn mini-desk-only"
          onClick={() => p.skip(-15)}
          aria-label="Back 15 seconds"
        >
          <Icon name="skip-back" size={22} />
        </button>
        <button
          className={`mini-play${p.buffering ? ' buffering' : ''}`}
          onClick={p.toggle}
          aria-label={p.playing ? 'Pause' : 'Play'}
        >
          <Icon name={p.playing ? 'pause' : 'play'} size={18} />
        </button>
        <button
          className="icon-btn mini-desk-only"
          onClick={() => p.skip(30)}
          aria-label="Forward 30 seconds"
        >
          <Icon name="skip-fwd" size={22} />
        </button>
        <button
          className="icon-btn mini-desk-only"
          onClick={() => p.setFocus(true)}
          aria-label="Open the player"
        >
          <Icon name="expand" />
        </button>
        <button
          className="icon-btn"
          onClick={() => p.stop('finish')}
          aria-label={p.learning ? 'End session' : 'End practice'}
          title={p.learning ? 'End session - your place is kept' : 'End practice'}
        >
          <Icon name="x" />
        </button>
      </div>
    </div>
  );
}

// ── Full player ────────────────────────────────────────────────────────────

export function FocusMode() {
  const p = usePlayer();
  const open = !!p.item && p.focus;
  useScrollLock(open);
  const [sheet, setSheet] = useState<null | 'settings' | 'tracks'>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // The player context changes several times a second while playing; the drag
  // handler must not be torn down mid-gesture, so it reads this ref instead.
  const minimise = useRef(() => p.setFocus(false));
  minimise.current = () => p.setFocus(false);

  // Drag down to minimise, as with any native full-screen player. A drag that
  // starts on the seek bar, a volume slider or a sheet keeps its own gesture;
  // anywhere else the page never scrolls (iOS would otherwise rubber-band the
  // whole player), and a downward pull moves the player with the finger. Let
  // go past a sixth of the screen, or with a flick, and it slides down into
  // the mini player; otherwise it springs back.
  useEffect(() => {
    const el = rootRef.current;
    if (!open || !el) return;
    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    let armed = false;
    let dragging = false;
    const ease = 'cubic-bezier(0.32, 0.72, 0, 1)';
    const place = (dy: number) => {
      const y = Math.max(0, dy);
      const k = Math.min(1, y / el.offsetHeight);
      el.style.transform = `translate3d(0, ${y}px, 0) scale(${1 - k * 0.06})`;
      el.style.borderRadius = `${Math.min(34, y / 4)}px`;
    };
    const onStart = (e: TouchEvent) => {
      const t = e.target as HTMLElement;
      armed = e.touches.length === 1 && !t.closest('.slider, .sheet');
      dragging = false;
      if (!armed) return;
      startY = lastY = e.touches[0]!.clientY;
      lastT = performance.now();
      velocity = 0;
    };
    const onMove = (e: TouchEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('.slider, .sheet')) return;
      e.preventDefault();
      if (!armed) return;
      const y = e.touches[0]!.clientY;
      const now = performance.now();
      velocity = (y - lastY) / Math.max(1, now - lastT);
      lastY = y;
      lastT = now;
      if (!dragging && y - startY > 8) {
        dragging = true;
        el.style.transition = 'none';
        el.style.animation = 'none';
        startY = y;
      }
      if (dragging) place(y - startY);
    };
    const onEnd = () => {
      if (!dragging) {
        armed = false;
        return;
      }
      dragging = false;
      armed = false;
      const dy = lastY - startY;
      if (performance.now() - lastT > 90) velocity = 0;
      if (dy > el.offsetHeight / 6 || velocity > 0.55) {
        el.style.transition = `transform 280ms ${ease}, border-radius 280ms ${ease}`;
        el.style.transform = 'translate3d(0, 100%, 0) scale(0.94)';
        window.setTimeout(() => minimise.current(), 260);
      } else {
        el.style.transition = `transform 360ms ${ease}, border-radius 360ms ${ease}`;
        el.style.transform = '';
        el.style.borderRadius = '';
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [open]);

  // Escape minimises (a sheet on top handles its own Escape first).
  useEffect(() => {
    if (!open || sheet) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') p.setFocus(false);
      if (e.key === ' ' && !(e.target as HTMLElement).closest('button, input')) {
        e.preventDefault();
        p.toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, sheet, p]);

  if (!open || !p.item) return null;
  const it = p.item;
  const multi = it.tracks.length > 1;
  const s = p.settings;
  const settling = p.leadInRemaining !== null;
  // Bells and the end timer are practice tools; a lesson does not need them in reach.
  const learning = it.type === 'course' || it.type === 'talk';
  // Practice tools belong to a practice: a meditation, or a meditation that
  // is part of a course. A lesson - or a meditation's intro - gets none.
  const practiceNow = p.track ? p.track.role === 'practice' : !learning;
  const part = it.type === 'course' ? 'Lesson' : 'Track';

  return (
    <div
      className="fp"
      role="dialog"
      aria-modal="true"
      aria-label={`Playing ${it.title}`}
      ref={rootRef}
    >
      <span className="fp-grab" aria-hidden="true" />
      <div className="fp-wash" aria-hidden="true">
        {it.coverId ? (
          <img src={`/api/media/asset/${it.coverId}?w=320`} alt="" />
        ) : (
          <div className="fp-wash-gen" />
        )}
      </div>

      <header className="fp-top">
        <button
          className="fp-round"
          onClick={() => p.setFocus(false)}
          aria-label="Minimise the player"
        >
          <Icon name="chevron-down" size={22} />
        </button>
        <div className="fp-top-mid">
          <span className="fp-kicker">
            {practiceNow ? 'Now practicing' : p.isVideo ? 'Now watching' : 'Now studying'}
          </span>
          {p.wakeLockOn && (
            <span className="fp-awake" title="The screen stays on while this plays">
              <Icon name="sun" size={12} /> Screen stays on
            </span>
          )}
        </div>
        {practiceNow ? (
          <button
            className="fp-round"
            onClick={() => setSheet('settings')}
            aria-label="Practice settings"
          >
            <Icon name="sliders" size={20} />
          </button>
        ) : (
          <span className="fp-round-spacer" aria-hidden="true" />
        )}
      </header>

      <div className="fp-body">
        <div className={`fp-art${p.playing ? ' is-playing' : ''}${p.isVideo ? ' is-video' : ''}`}>
          {p.isVideo && p.videoEl ? (
            <VideoStage el={p.videoEl} />
          ) : (
            <Cover coverId={it.coverId} title={it.title} creator={it.creator} size={640} />
          )}
        </div>

        <div className="fp-info">
          <h1 className="fp-title">{it.title}</h1>
          <p className="fp-sub">
            {it.creator}
            {multi && p.track && (
              <>
                <span className="dot" aria-hidden="true">
                  ·
                </span>
                {part} {p.trackIndex + 1} of {it.tracks.length}
              </>
            )}
          </p>
          {multi && p.track && <p className="fp-track">{p.track.title}</p>}
          {p.learning && multi && <CourseProgress />}
        </div>

        {settling ? (
          <div className="fp-settle" role="status">
            <div className="fp-settle-ring">
              <span>{p.leadInRemaining}</span>
            </div>
            <p>Arrive. The audio begins on its own.</p>
          </div>
        ) : (
          <Scrubber />
        )}

        <div className="fp-transport">
          {multi ? (
            <button
              className="fp-t"
              onClick={p.prevTrack}
              aria-label="Previous track"
              disabled={p.trackIndex === 0 && p.position < 5}
            >
              <Icon name="prev" size={22} />
            </button>
          ) : (
            <span className="fp-t-spacer" />
          )}
          <button
            className="fp-t"
            onClick={() => p.skip(-15)}
            aria-label="Back 15 seconds"
            disabled={settling}
          >
            <Icon name="skip-back" size={30} />
          </button>
          <button
            className={`fp-play${p.buffering ? ' buffering' : ''}`}
            onClick={p.toggle}
            aria-label={settling ? 'Skip the settling time' : p.playing ? 'Pause' : 'Play'}
          >
            <Icon name={p.playing ? 'pause' : 'play'} size={30} />
          </button>
          <button
            className="fp-t"
            onClick={() => p.skip(30)}
            aria-label="Forward 30 seconds"
            disabled={settling}
          >
            <Icon name="skip-fwd" size={30} />
          </button>
          {multi ? (
            <button
              className="fp-t"
              onClick={p.nextTrack}
              aria-label="Next track"
              disabled={p.trackIndex >= it.tracks.length - 1}
            >
              <Icon name="next" size={22} />
            </button>
          ) : (
            <span className="fp-t-spacer" />
          )}
        </div>

        <div className="fp-chips">
          {p.learning && p.track && (
            <DoneChip
              key={p.track.id}
              track={p.track}
              done={p.completedIds.has(p.track.id)}
              onToggle={() => {
                const t = p.track!;
                void p.setTrackDone(t.id, !p.completedIds.has(t.id)).catch(() => {});
              }}
            />
          )}
          <button
            className="fp-chip"
            onClick={() => {
              const i = SPEEDS.indexOf(s.speed as (typeof SPEEDS)[number]);
              p.updateSettings({ speed: SPEEDS[(i + 1) % SPEEDS.length] });
            }}
            aria-label={`Speed ${speedLabel(s.speed)} - tap to change`}
          >
            <Icon name="gauge" size={18} />
            {speedLabel(s.speed)}
          </button>
          {practiceNow && (
            <>
              <button
                className={`fp-chip${s.bellsEveryMin ? ' on' : ''}`}
                onClick={() => setSheet('settings')}
                aria-label={`Interval bell: ${s.bellsEveryMin ? `every ${s.bellsEveryMin} minutes` : 'off'}`}
              >
                <Icon name="bell" size={18} />
                {s.bellsEveryMin ? `${s.bellsEveryMin} min` : 'Bells'}
              </button>
              <button
                className={`fp-chip${s.endAfterMin ? ' on' : ''}`}
                onClick={() => setSheet('settings')}
                aria-label={`End timer: ${s.endAfterMin ? `after ${s.endAfterMin} minutes` : 'off'}`}
              >
                <Icon name="moon" size={18} />
                {s.endAfterMin
                  ? `${minLabel(Math.max(1, Math.ceil(s.endAfterMin - p.practiceElapsed / 60)))} left`
                  : 'Timer'}
              </button>
            </>
          )}
          {p.isVideo && p.videoEl && <FullscreenChip el={p.videoEl} />}
          {multi && (
            <button
              className="fp-chip"
              onClick={() => setSheet('tracks')}
              aria-label={it.type === 'course' ? 'Lessons' : 'Tracks'}
            >
              <Icon name="list" size={18} />
              {it.type === 'course' ? 'Lessons' : 'Tracks'}
            </button>
          )}
        </div>
      </div>

      <footer className="fp-foot">
        <span className="fp-elapsed">
          {formatClock(p.practiceElapsed)} {p.learning ? 'studied' : 'practiced'}
        </span>
        <button
          className="fp-end"
          onClick={() => p.stop('finish')}
          title={p.learning ? 'Stop here - your place is kept for next time' : undefined}
        >
          {p.learning ? 'End session' : 'End practice'}
        </button>
      </footer>

      {sheet === 'settings' && <PracticeSettingsSheet onClose={() => setSheet(null)} />}
      {sheet === 'tracks' && <TrackListSheet onClose={() => setSheet(null)} />}
    </div>
  );
}

// ── Video ──────────────────────────────────────────────────────────────────

/**
 * Shows the player's one video element. It lives in the provider and is only
 * moved here while the full player is open, then parked again - it is never
 * removed from the document, so minimising does not stop a talk.
 */
function VideoStage({ el }: { el: HTMLVideoElement }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const stage = ref.current;
    if (!stage) return;
    stage.appendChild(el);
    return () => {
      document.getElementById('zp-video-home')?.appendChild(el);
    };
  }, [el]);
  return <div className="fp-video" ref={ref} />;
}

type WebkitVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

function FullscreenChip({ el }: { el: HTMLVideoElement }) {
  const v = el as WebkitVideo;
  return (
    <button
      className="fp-chip"
      onClick={() => {
        if (el.requestFullscreen)
          void el.requestFullscreen().catch(() => v.webkitEnterFullscreen?.());
        else v.webkitEnterFullscreen?.();
      }}
      aria-label="Full screen"
    >
      <Icon name="expand" size={18} />
      Full screen
    </button>
  );
}

/**
 * A course's lessons as a row of segments - done, current, still ahead. The
 * tick for the one playing now is with the buttons below (DoneChip).
 */
function CourseProgress() {
  const p = usePlayer();
  if (!p.item) return null;
  const tracks = p.item.tracks;
  const done = tracks.filter((t) => p.completedIds.has(t.id)).length;
  return (
    <div className="fp-course" aria-label={`${done} of ${tracks.length} lessons done`}>
      <div className="fp-course-bar">
        {tracks.length <= 40 ? (
          tracks.map((t, i) => (
            <span
              key={t.id}
              className={p.completedIds.has(t.id) ? 'done' : i === p.trackIndex ? 'now' : ''}
            />
          ))
        ) : (
          <span
            className="done"
            style={{ flex: 'none', inlineSize: `${(done / tracks.length) * 100}%` }}
          />
        )}
      </div>
    </div>
  );
}

// ── Track list ─────────────────────────────────────────────────────────────

function TrackListSheet({ onClose }: { onClose: () => void }) {
  const p = usePlayer();
  if (!p.item) return null;
  const course = p.item.type === 'course';
  // In a course or talk each part can be ticked done or not right here; a
  // meditation's tracks are simply played.
  const tickable = p.learning;
  const done = p.item.tracks.filter((t) => p.completedIds.has(t.id)).length;
  return (
    <Sheet title={course ? 'Lessons' : 'Tracks'} onClose={onClose} labelId="tracks-title">
      {tickable && (
        <p className="tl-note">
          {done} of {p.item.tracks.length} done · tap a circle to mark one done or not
        </p>
      )}
      <ol className="tl">
        {p.item.tracks.map((t, i) => {
          const current = i === p.trackIndex;
          const isDone = p.completedIds.has(t.id);
          return (
            <li key={t.id} className={tickable ? `tl-item${isDone ? ' done' : ''}` : undefined}>
              {tickable && (
                <DoneTick
                  track={t}
                  done={isDone}
                  onToggle={() => void p.setTrackDone(t.id, !isDone).catch(() => {})}
                />
              )}
              <button
                className={`tl-row${current ? ' current' : ''}`}
                aria-current={current || undefined}
                onClick={() => {
                  p.playTrack(i);
                  onClose();
                }}
              >
                <span className="tl-n">
                  {current && p.playing ? (
                    <span className="tl-eq" aria-hidden="true" />
                  ) : !tickable && isDone ? (
                    <Icon name="check-circle" size={18} />
                  ) : (
                    i + 1
                  )}
                </span>
                <span className="tl-t">{t.title}</span>
                <span className="tl-d">{t.durationSec ? formatClock(t.durationSec) : ''}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </Sheet>
  );
}

// ── Practice settings ──────────────────────────────────────────────────────

function Seg<T extends number>({
  options,
  value,
  onChange,
  label,
  render,
}: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  render: (v: T) => string;
}) {
  return (
    <div className="seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o}
          role="radio"
          aria-checked={o === value}
          className={`seg-opt${o === value ? ' on' : ''}`}
          onClick={() => onChange(o)}
        >
          {render(o)}
        </button>
      ))}
    </div>
  );
}

function Section({
  art,
  title,
  hint,
  value,
  children,
}: {
  art: string;
  title: string;
  hint: string;
  value?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="ps-sec">
      <div className="ps-head">
        <img className="ps-art" src={`/art/${art}.webp`} alt="" width={56} height={56} />
        <div className="ps-text">
          <h3>
            {title}
            {value && <span className="ps-value">{value}</span>}
          </h3>
          <p>{hint}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

export function PracticeSettingsSheet({ onClose }: { onClose: () => void }) {
  const p = usePlayer();
  const s = p.settings;
  return (
    <Sheet title="Practice settings" onClose={onClose} labelId="ps-title">
      <div className="ps">
        <div className="ps-awake">
          <span className="ps-awake-ic">
            <Icon name="sun" size={20} />
          </span>
          <div className="ps-text">
            <h3>Keep the screen on</h3>
            <p>
              {p.wakeLockSupported
                ? 'While a practice plays, so auto-lock never cuts a long sit short.'
                : 'This browser cannot hold the screen on; audio still plays with the screen off.'}
            </p>
            {p.wakeLockNote && s.keepAwake && p.wakeLockSupported && (
              <p className="ps-warn">{p.wakeLockNote}</p>
            )}
          </div>
          <Switch
            checked={s.keepAwake}
            onChange={(v) => p.updateSettings({ keepAwake: v })}
            label="Keep the screen on while practicing"
            disabled={!p.wakeLockSupported}
          />
        </div>
        <Section
          art="set-arrive"
          title="Settle in"
          hint="A quiet pause before the audio starts."
          value={s.leadInSec ? LEAD_INS.find((l) => l.v === s.leadInSec)?.label : 'Off'}
        >
          <Seg
            label="Settling time"
            options={LEAD_INS.map((l) => l.v)}
            value={s.leadInSec as (typeof LEAD_INS)[number]['v']}
            onChange={(v) => p.updateSettings({ leadInSec: v })}
            render={(v) => LEAD_INS.find((l) => l.v === v)!.label}
          />
        </Section>

        <Section
          art="set-bell"
          title="Interval bell"
          hint="A soft bowl to mark the time as you sit."
          value={s.bellsEveryMin ? `every ${s.bellsEveryMin} min` : 'Off'}
        >
          <Seg
            label="Interval bell"
            options={BELLS}
            value={s.bellsEveryMin as (typeof BELLS)[number]}
            onChange={(v) => p.updateSettings({ bellsEveryMin: v })}
            render={minShort}
          />
          <button className="ps-try" onClick={() => playBell(s.volume)}>
            <Icon name="bell" size={14} /> Hear the bell
          </button>
        </Section>

        <Section
          art="set-end"
          title="End timer"
          hint="Fades the audio out, then a closing bell. Good for sleep."
          value={s.endAfterMin ? `after ${minLabel(s.endAfterMin)}` : 'Off'}
        >
          <Seg
            label="End timer"
            options={END_AFTER}
            value={s.endAfterMin as (typeof END_AFTER)[number]}
            onChange={(v) => p.updateSettings({ endAfterMin: v })}
            render={minShort}
          />
        </Section>

        <Section
          art="set-sound"
          title="Sound"
          hint="Pace and level of the recording."
          value={speedLabel(s.speed)}
        >
          <Seg
            label="Speed"
            options={SPEEDS}
            value={s.speed as (typeof SPEEDS)[number]}
            onChange={(v) => p.updateSettings({ speed: v })}
            render={speedLabel}
          />
          <div className="ps-volume">
            <Icon name="volume-low" size={18} />
            <Slider
              value={Math.round(s.volume * 100)}
              max={100}
              keyStep={5}
              onInput={(v) => p.updateSettings({ volume: v / 100 })}
              onCommit={(v) => p.updateSettings({ volume: v / 100 })}
              label="Volume"
              valueText={`${Math.round(s.volume * 100)}%`}
            />
            <Icon name="volume" size={18} />
          </div>
        </Section>
      </div>
    </Sheet>
  );
}
